/**
 * Pets faces, on every page (any quick view or product page with a face option).
 * Loaded from layout/theme.liquid; it needs only Lumi.emit. The other pet product page parts (the ship-from line,
 * Pick 2, the size finder and the gift note) are in assets/pets-product-page.js, which only the Pets product
 * template loads.
 *
 * <pets-faces> (snippets/pets-faces.liquid)
 *   select mode: each face checks the matching radio of the theme's <variant-picker> in the same scope
 *   (the closest [data-variant-scope] or quick-view-panel) and fires a bubbling change, so price, photo
 *   and Add to cart follow. When every value of the option has a face, the original fieldset hides.
 *   Faces mirror the radios: pressed when checked, sold out when the picker marks the value unavailable.
 *   open mode: each face emits "lumi:pets:face" with { handle, option, value, variantId }.
 *   pick mode: plain face buttons; the Pick 2 block (<pets-pick-n>) handles their clicks.
 */
(function () {
  const Lumi = (window.Lumi = window.Lumi || {});

  class PetsFaces extends HTMLElement {
    connectedCallback() {
      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);
      if (this.dataset.mode !== 'select') return;

      const scope = this.closest('[data-variant-scope]') || this.closest('quick-view-panel') || this.closest('form');
      this.fieldset = scope && scope.querySelector(`variant-picker [data-option-index="${CSS.escape(this.dataset.optionIndex || '')}"]`);
      if (!this.fieldset) return;
      this.hidden = false;
      const radios = [...this.fieldset.querySelectorAll('input[type="radio"]')];
      const faces = this.faces();
      // Every value needs a face before the plain picker can go; otherwise both stay.
      this.fieldset.hidden = faces.length > 0 && radios.every((r) => faces.some((f) => f.dataset.value === r.value));

      this.onVariant = (e) => {
        if (!e.detail || !e.detail.picker || !e.detail.picker.contains(this.fieldset)) return;
        this.sync();
      };
      document.addEventListener('lumi:variant:changed', this.onVariant);
      this.sync();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.onVariant) document.removeEventListener('lumi:variant:changed', this.onVariant);
    }

    faces() {
      return [...this.querySelectorAll('[data-pets-face]')];
    }

    radioFor(value) {
      if (!this.fieldset) return null;
      return [...this.fieldset.querySelectorAll('input[type="radio"]')].find((r) => r.value === value) || null;
    }

    onClick(e) {
      const face = e.target.closest('[data-pets-face]');
      if (!face) return;
      if (this.dataset.mode === 'open') {
        Lumi.emit('lumi:pets:face', {
          handle: this.dataset.handle,
          option: this.dataset.option,
          value: face.dataset.value,
          variantId: Number(face.dataset.variantId) || null,
          trigger: face,
        });
        return;
      }
      if (this.dataset.mode !== 'select') return;
      const radio = this.radioFor(face.dataset.value);
      if (!radio || radio.checked) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      this.sync();
    }

    // Mirror the radios: pressed when checked, sold out when the picker marks the value unavailable.
    sync() {
      let current = '';
      this.faces().forEach((face) => {
        const radio = this.radioFor(face.dataset.value);
        if (!radio) return;
        const unavailable = radio.hasAttribute('data-unavailable');
        face.setAttribute('aria-pressed', String(radio.checked));
        face.classList.toggle('pets-face--soldout', unavailable);
        face.dataset.available = String(!unavailable);
        const tag = face.querySelector('[data-soldout-tag]');
        if (tag) tag.hidden = !unavailable;
        if (radio.checked) current = radio.value;
      });
      const label = this.querySelector('[data-pets-face-current]');
      if (label && current) label.textContent = current;
    }
  }

  if (!customElements.get('pets-faces')) customElements.define('pets-faces', PetsFaces);
})();
